type AdvanceFilterEntry = { id: string; value?: any };

type CampaignPlanOption = { _id: string; mediaAdvertiserUser: string };

type InsertionOrderOption = { _id: string; campaignPlan: string };

export function filterCampaignPlansByAdvertiser<T extends CampaignPlanOption>(
  campaignPlans: T[] | undefined,
  advertiserIds: string[] | undefined
): T[] | undefined {
  if (!advertiserIds?.length) {
    return campaignPlans;
  }
  return campaignPlans?.filter(campaignPlan => advertiserIds.includes(campaignPlan.mediaAdvertiserUser));
}

export function filterInsertionOrdersByCampaignPlans<T extends InsertionOrderOption>(
  insertionOrders: T[] | undefined,
  campaignPlanIds: string[] | undefined
): T[] | undefined {
  if (!campaignPlanIds?.length) {
    return insertionOrders;
  }
  return insertionOrders?.filter(insertionOrder => campaignPlanIds.includes(insertionOrder.campaignPlan));
}

export function reconcileCascadingAdvanceFilters(
  advanceFilters: AdvanceFilterEntry[],
  campaignPlans: CampaignPlanOption[] | undefined,
  insertionOrders?: InsertionOrderOption[] | undefined
): AdvanceFilterEntry[] {
  const advertiserIds: string[] | undefined = advanceFilters.find(filter => filter.id === 'advertiserIds')?.value;
  const campaignPlansFilter = advanceFilters.find(filter => filter.id === 'campaignPlans');
  const insertionOrdersFilter = advanceFilters.find(filter => filter.id === 'insertionOrders');

  const validCampaignPlans = filterCampaignPlansByAdvertiser(campaignPlans, advertiserIds);
  const validCampaignPlanIds = validCampaignPlans && new Set(validCampaignPlans.map(({ _id }) => _id));

  const reconciledCampaignPlanIds: string[] | undefined =
    validCampaignPlanIds && campaignPlansFilter?.value?.length
      ? campaignPlansFilter.value.filter((id: string) => validCampaignPlanIds.has(id))
      : campaignPlansFilter?.value;

  const effectiveCampaignPlanIds = reconciledCampaignPlanIds?.length
    ? reconciledCampaignPlanIds
    : validCampaignPlans?.map(({ _id }) => _id);

  const validInsertionOrders = filterInsertionOrdersByCampaignPlans(insertionOrders, effectiveCampaignPlanIds);
  const validInsertionOrderIds = validInsertionOrders && new Set(validInsertionOrders.map(({ _id }) => _id));

  const reconciledInsertionOrderIds: string[] | undefined =
    validInsertionOrderIds && insertionOrdersFilter?.value?.length
      ? insertionOrdersFilter.value.filter((id: string) => validInsertionOrderIds.has(id))
      : insertionOrdersFilter?.value;

  const campaignPlansChanged = (reconciledCampaignPlanIds?.length ?? 0) !== (campaignPlansFilter?.value?.length ?? 0);
  const insertionOrdersChanged =
    (reconciledInsertionOrderIds?.length ?? 0) !== (insertionOrdersFilter?.value?.length ?? 0);

  if (!campaignPlansChanged && !insertionOrdersChanged) {
    return advanceFilters;
  }

  return advanceFilters.map(filter => {
    if (filter.id === 'campaignPlans' && campaignPlansChanged) {
      return { ...filter, value: reconciledCampaignPlanIds?.length ? reconciledCampaignPlanIds : undefined };
    }
    if (filter.id === 'insertionOrders' && insertionOrdersChanged) {
      return { ...filter, value: reconciledInsertionOrderIds?.length ? reconciledInsertionOrderIds : undefined };
    }
    return filter;
  });
}
